import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { TestModuleService } from './test-module.service';
import { TestModule } from '../models/test-module.model';

/**
 * Leichter, Signal-basierter In-Memory-Store für Testmodule (NEX-691). Sobald ein Modul
 * im Modul-Edit-Drawer gespeichert wird, spiegelt ein {@link upsert} die Änderung an alle
 * offenen Ansichten (Testfall-Testschritt-Tabelle, andere Drawer-Ebenen), die dieses Modul
 * referenzieren — ohne Reload. Kein Persist-Layer, nur reaktive Zwischenspeicherung.
 */
@Injectable({ providedIn: 'root' })
export class TestModuleStore {
  private readonly moduleService = inject(TestModuleService);
  private readonly entries = new Map<string, WritableSignal<TestModule | null>>();
  private readonly fetched = new Set<string>();

  /**
   * Reaktives Signal für ein Modul; lädt beim ersten Zugriff lazy nach. Liefert bei erneutem
   * {@code get} dieselbe Signal-Referenz, bis {@link invalidate} den Eintrag verwirft.
   */
  get(projectId: string, id: string): Signal<TestModule | null> {
    const entry = this.entry(id);
    if (!this.fetched.has(id)) {
      this.fetched.add(id);
      this.moduleService.getById(projectId, id).subscribe({
        next: (module) => entry.set(module),
        error: () => this.fetched.delete(id),
      });
    }
    return entry;
  }

  /**
   * Nicht-ladendes Signal: spiegelt nur bereits bekannte Live-Daten (z. B. nach einem
   * Drawer-Save) und stößt selbst keinen Fetch an — für Consumer, die den Snapshot der
   * eingebundenen Zeile nur überschreiben wollen, wenn frischere Daten vorliegen.
   */
  watch(id: string): Signal<TestModule | null> {
    return this.entry(id);
  }

  upsert(module: TestModule): void {
    this.entry(module.id).set(module);
    this.fetched.add(module.id);
  }

  invalidate(id: string): void {
    this.entries.delete(id);
    this.fetched.delete(id);
  }

  private entry(id: string): WritableSignal<TestModule | null> {
    let entry = this.entries.get(id);
    if (!entry) {
      entry = signal<TestModule | null>(null);
      this.entries.set(id, entry);
    }
    return entry;
  }
}
